import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format as formatDate } from "date-fns";
import { Search, Plus, ScanBarcode, ArrowUpDown, Boxes, AlertTriangle, ChevronLeft, Layers, ReceiptText, Pencil, Calculator, Calendar as CalendarIcon, Loader2, Trash2 } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { PageContainer } from "@/components/layout/PageContainer";
import { ProductImageGenerateDialog } from "@/components/products/ProductImageGenerateDialog";
import { ProductArtwork } from "@/components/products/ProductArtwork";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, useWorkspace } from "@/lib/store";
import { productStockStatus } from "@/lib/formatters";
import { useLocalization } from "@/lib/localization";
import { formatOperatingCurrency } from "@/lib/money";
import { getProductFormCopy } from "@/lib/productFormCopy";
import { cn } from "@/lib/utils";
import { isProductLimitError } from "@/lib/productLimits";
import { getInventoryParityCopy } from "@/lib/inventoryParityCopy";
import { getInventoryDraftNumber, isProductSetupNeeded } from "@/lib/quickSell";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import { toast } from "sonner";
import { notifyInsufficientCredits } from "@/lib/apiClient";
import { z } from "zod";
import { useTutorial } from "@/features/tutorial/TutorialContext";

function requiredNumber(value) {
  const text = String(value ?? "").trim();
  return text ? Number(text) : undefined;
}

function optionalNumber(value) {
  const text = String(value ?? "").trim();
  return text ? Number(text) : 0;
}

function createInventoryProductSchema(copy) {
  return z.object({
    barcode: z.string().trim().optional(),
    cost: z.preprocess(
      optionalNumber,
      z.number({ invalid_type_error: copy.validation.costValid }).min(0, copy.validation.costNonNegative)
    ),
    lowStockAt: z.preprocess(
      optionalNumber,
      z.number({ invalid_type_error: copy.validation.lowStockWhole })
        .int(copy.validation.lowStockWhole)
        .min(0, copy.validation.lowStockNonNegative)
    ),
    name: z.string().trim().min(1, copy.validation.productNameRequired),
    price: z.preprocess(
      requiredNumber,
      z.number({
        required_error: copy.validation.priceRequired,
        invalid_type_error: copy.validation.priceValid,
      }).positive(copy.validation.priceAboveZero)
    ),
    stock: z.preprocess(
      requiredNumber,
      z.number({
        required_error: copy.validation.stockRequired,
        invalid_type_error: copy.validation.stockWhole,
      }).int(copy.validation.stockWhole).min(0, copy.validation.stockNonNegative)
    ),
  });
}

function createProfitCalculatorSchema(copy) {
  return z.object({
    cost: z.string().trim().min(1, copy.validation.costRequired).refine((value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0;
    }, copy.validation.costValid),
    sellingPrice: z.string().trim().min(1, copy.validation.priceRequired).refine((value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= 0;
    }, copy.validation.priceValid),
    rangeStart: z.string().optional(),
    rangeEnd: z.string().optional(),
  }).superRefine((value, ctx) => {
    if (!value.rangeStart || !value.rangeEnd) return;
    const start = new Date(`${value.rangeStart}T00:00:00`);
    const end = new Date(`${value.rangeEnd}T23:59:59.999`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: copy.validDateRange,
        path: ["rangeEnd"],
      });
    }
  });
}

function getFieldErrors(error) {
  const flattened = error.flatten().fieldErrors;
  return Object.fromEntries(
    Object.entries(flattened).map(([field, messages]) => [field, messages?.[0] || ""])
  );
}

function parseFormDate(value) {
  if (!value) return undefined;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function toFormDateValue(date) {
  return date ? formatDate(date, "yyyy-MM-dd") : "";
}

function formatCurrencyLocal(value, locale) {
  return formatOperatingCurrency(value, locale);
}

function formatDateLocal(value, locale) {
  if (!value) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(new Date(value));
}

function formatTimeLocal(value, locale) {
  return new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatRelativeLocal(value, locale) {
  const target = new Date(value);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (Math.abs(diffMinutes) < 60) return rtf.format(diffMinutes, "minute");
  const diffHours = Math.round(diffMs / 3600000);
  if (Math.abs(diffHours) < 24) return rtf.format(diffHours, "hour");
  const diffDays = Math.round(diffMs / 86400000);
  return rtf.format(diffDays, "day");
}

export default function Inventory() {
  const PAGE_SIZE = 40;
  const { session } = useAuth();
  const { archiveProduct, cleanProductImage, credits, inventoryAllocations, products, sales, saveProduct, addStock, generateProductImage, staff } = useWorkspace();
  const { language, locale, messages } = useLocalization();
  const tutorial = useTutorial();
  const copy = messages.inventory;
  const formCopy = getProductFormCopy(language);
  const parityCopy = getInventoryParityCopy(language);
  const location = useLocation();
  const navigate = useNavigate();
  const { productId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const legacyProductId = searchParams.get("productId");
  const [scope, setScope] = useState(searchParams.get("scope") || "all");
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [filter, setFilter] = useState(searchParams.get("filter") || "all");
  const [draft, setDraft] = useState(createProductDraft());
  const [productFormErrors, setProductFormErrors] = useState({});
  const [imageDialogProduct, setImageDialogProduct] = useState(null);
  const [processingImageIds, setProcessingImageIds] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const searchSuffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
  const isCreateRoute = location.pathname === "/inventory/new";
  const isEditRoute = Boolean(productId) && location.pathname === `/inventory/${productId}/edit`;
  const detailView = Boolean(productId) && !isEditRoute
    ? location.pathname === `/inventory/${productId}/stock`
      ? "stock"
      : location.pathname === `/inventory/${productId}/allocation`
        ? "allocation"
        : location.pathname === `/inventory/${productId}/transactions`
          ? "transactions"
          : location.pathname === `/inventory/${productId}/profit`
            ? "profit"
          : location.pathname === `/inventory/${productId}`
            ? "overview"
            : null
    : null;

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (scope === "today" && (p.soldToday || 0) === 0) return false;
      if (filter === "setup_needed" && !isProductSetupNeeded(p)) return false;
      if (filter !== "all" && filter !== "setup_needed" && productStockStatus(p) !== filter) return false;
      if (!query) return true;
      return p.name.toLowerCase().includes(query.toLowerCase()) || (p.barcode || "").includes(query);
    });
  }, [products, scope, filter, query]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedProducts = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page, PAGE_SIZE]);

  const counts = useMemo(() => ({
    all: products.length,
    in_stock: products.filter((p) => productStockStatus(p) === "in_stock").length,
    low_stock: products.filter((p) => productStockStatus(p) === "low_stock").length,
    stock_out: products.filter((p) => productStockStatus(p) === "stock_out").length,
    setup_needed: products.filter(isProductSetupNeeded).length,
  }), [products]);
  const selectedProduct = useMemo(() => (
    productId ? products.find((product) => product.id === productId) || null : null
  ), [productId, products]);

  useEffect(() => {
    if (!tutorial.active || tutorial.progress?.currentStep !== "inventory_update" || !tutorial.focus.productId || productId) return;
    const target = products.find((product) => product.id === tutorial.focus.productId);
    if (!target) return;
    setScope("all");
    setFilter("all");
    setQuery(target.name);
    setPage(1);
  }, [productId, products, tutorial.active, tutorial.focus.productId, tutorial.progress?.currentStep]);
  const selectedAllocations = useMemo(() => {
    if (!selectedProduct) return [];
    return inventoryAllocations
      .filter((allocation) => allocation.productId === selectedProduct.id)
      .map((allocation) => {
        const member = staff.find((item) => item.id === allocation.userId);
        const isOwner = allocation.userId === session?.userId;
        return {
          name: isOwner
            ? `${session?.shopName || copy.owner} (${copy.owner})`
            : member
              ? `${member.name} (${member.role === "Manager" ? copy.manager : copy.staff})`
              : allocation.userId.slice(0, 8),
          qty: Number(allocation.allocatedQuantity || 0),
        };
      })
      .sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name));
  }, [copy.manager, copy.owner, copy.staff, inventoryAllocations, selectedProduct, session?.shopName, session?.userId, staff]);

  useEffect(() => {
    if (!legacyProductId || productId) return;
    navigate(`/inventory/${legacyProductId}${searchSuffix}`, { replace: true });
  }, [legacyProductId, navigate, productId, searchSuffix]);

  useEffect(() => {
    if (!productId || isCreateRoute) return;
    if (products.length > 0 && !selectedProduct) {
      navigate(`/inventory${searchSuffix}`, { replace: true });
    }
  }, [isCreateRoute, navigate, productId, products.length, searchSuffix, selectedProduct]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    scope && scope !== "all" ? nextParams.set("scope", scope) : nextParams.delete("scope");
    query ? nextParams.set("q", query) : nextParams.delete("q");
    filter && filter !== "all" ? nextParams.set("filter", filter) : nextParams.delete("filter");
    setSearchParams(nextParams, { replace: true });
  }, [filter, query, scope, setSearchParams]);

  useEffect(() => {
    setPage(1);
  }, [filter, query, scope]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (isEditRoute && selectedProduct) {
      setDraft(createProductDraft(selectedProduct));
      setProductFormErrors({});
    }
  }, [isEditRoute, selectedProduct]);

  const routeWithSearch = (path) => `${path}${searchSuffix}`;
  const closeSelectedProduct = () => navigate(routeWithSearch("/inventory"));

  const openImageDialog = (product) => {
    if (!product) {
      return;
    }
    setImageDialogProduct(product);
  };

  const handleGenerateProductImage = async ({ description = "", image = null, mode = "generate" } = {}) => {
    if (!imageDialogProduct || processingImageIds.has(imageDialogProduct.id)) {
      return;
    }

    const targetProduct = imageDialogProduct;
    setImageDialogProduct(null);
    setProcessingImageIds((prev) => {
      const next = new Set(prev);
      next.add(targetProduct.id);
      return next;
    });

    void (async () => {
      try {
        if (mode === "upload") {
          await cleanProductImage(targetProduct.id, image, description);
          toast.success(messages.common.productImageGenerated, { description: targetProduct.name });
        } else {
          await generateProductImage(targetProduct.id, {
            description: String(description || "").trim() || undefined,
            force: Boolean(targetProduct.url),
          });
          toast.info(messages.common.processingImage, { description: targetProduct.name });
        }
      } catch (error) {
        if (error?.code === "INSUFFICIENT_CREDITS") {
          notifyInsufficientCredits({ feature: "product_image", requiredUnits: 5_000 });
        } else {
          toast.error(error instanceof Error ? error.message : messages.common.productImageGenerateFailed);
        }
      } finally {
        setProcessingImageIds((prev) => {
          const next = new Set(prev);
          next.delete(targetProduct.id);
          return next;
        });
      }
    })();
  };

  const openCreatePage = (name = "", barcode = "") => {
    setDraft(createProductDraft({ barcode, name }));
    setProductFormErrors({});
    navigate(routeWithSearch("/inventory/new"));
  };

  const openEditPage = (product) => {
    setDraft(createProductDraft(product));
    setProductFormErrors({});
    navigate(routeWithSearch(`/inventory/${product.id}/edit`));
  };

  const updateDraftField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setProductFormErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const saveDraft = async () => {
    if (saving) {
      return;
    }

    const parsed = createInventoryProductSchema(copy).safeParse(draft);
    if (!parsed.success) {
      setProductFormErrors(getFieldErrors(parsed.error));
      toast.error(copy.checkRequiredProductDetails);
      return;
    }

    if (selectedProduct && isProductSetupNeeded(selectedProduct) && parsed.data.cost <= 0) {
      setProductFormErrors({ cost: copy.validation.costRequired });
      toast.error(copy.checkRequiredProductDetails);
      return;
    }

    setSaving(true);
    setProductFormErrors({});
    try {
      const openingStock = parsed.data.stock;
      const isEditing = Boolean(draft.id);
      const currentStock = isEditing ? Number(selectedProduct?.stock ?? 0) : 0;
      const savedProduct = await saveProduct(
        {
          barcode: parsed.data.barcode || null,
          cost: parsed.data.cost,
          id: draft.id || undefined,
          lastSoldAt: draft.lastSoldAt || "",
          lowStockAt: parsed.data.lowStockAt,
          name: parsed.data.name,
          price: parsed.data.price,
          status: draft.status || "Active",
          stock: openingStock,
          stockTrackingEnabled: true,
          url: draft.url || null,
          urlStatus: draft.urlStatus || "pending",
        },
        isEditing
          ? { stockDelta: openingStock - currentStock }
          : { initialStock: openingStock }
      );
      toast.success(draft.id ? copy.productUpdated : copy.productAdded);
      if (tutorial.active && tutorial.progress?.currentStep === "inventory_update") {
        await tutorial.productSetupCompleted(savedProduct?.id || draft.id);
      } else {
        navigate(routeWithSearch(draft.id ? `/inventory/${draft.id}` : "/inventory"));
      }
    } catch (error) {
      if (isProductLimitError(error)) {
        return;
      }
      toast.error(copy.saveFailed, {
        description: error instanceof Error ? error.message : copy.tryAgain,
      });
    } finally {
      setSaving(false);
    }
  };

  useBarcodeScanner({
    disabled: Boolean(selectedProduct) && !isEditRoute && !isCreateRoute,
    onScan: (barcode) => {
      const code = barcode.trim();
      const matched = products.find((product) => String(product.barcode || "").trim() === code);
      if (isCreateRoute || isEditRoute) {
        updateDraftField("barcode", code);
        toast.success(copy.barcodeAttached, { description: code });
        return;
      }
      if (matched) {
        navigate(routeWithSearch(`/inventory/${matched.id}`));
        setQuery("");
        toast.success(copy.productScanned, { description: matched.name });
        return;
      }

      setQuery(code);
      openCreatePage("", code);
      toast.info(copy.barcodeNotFound, { description: copy.barcodeNotFoundDescription });
    },
  });

  if (isCreateRoute || isEditRoute) {
    return (
      <>
        <ProductFormPage
          copy={copy}
          draft={draft}
          errors={productFormErrors}
          formCopy={formCopy}
          parityCopy={parityCopy}
          onBack={() => navigate(routeWithSearch(isEditRoute && selectedProduct ? `/inventory/${selectedProduct.id}` : "/inventory"))}
          onFieldChange={updateDraftField}
          onSave={saveDraft}
          saving={saving}
          setupNeeded={Boolean(selectedProduct && isProductSetupNeeded(selectedProduct))}
        />
        <ProductImageGenerateDialog
          open={Boolean(imageDialogProduct)}
          onOpenChange={(open) => {
            if (!open) {
              setImageDialogProduct(null);
            }
          }}
          onConfirm={handleGenerateProductImage}
          product={imageDialogProduct}
          balance={credits}
        />
      </>
    );
  }

  if (selectedProduct && detailView) {
    return (
      <>
        <ProductDetailPage
          allocationRows={selectedAllocations}
          archiveCopy={parityCopy}
          copy={copy}
          currentView={detailView}
          locale={locale}
          isGeneratingImage={processingImageIds.has(selectedProduct.id)}
          product={selectedProduct}
          onGenerateImage={openImageDialog}
          onAddStock={addStock}
          onArchive={async (product) => {
            await archiveProduct(product.id);
            toast.success(parityCopy.archived);
            closeSelectedProduct();
          }}
          onBack={closeSelectedProduct}
          onEdit={openEditPage}
          onOpenView={(view) => navigate(routeWithSearch(view === "overview" ? `/inventory/${selectedProduct.id}` : `/inventory/${selectedProduct.id}/${view}`))}
          sales={sales}
        />
        <ProductImageGenerateDialog
          open={Boolean(imageDialogProduct)}
          onOpenChange={(open) => {
            if (!open) {
              setImageDialogProduct(null);
            }
          }}
          onConfirm={handleGenerateProductImage}
          product={imageDialogProduct}
          balance={credits}
        />
      </>
    );
  }

  return (
    <>
      <PageContainer>
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{copy.inventoryTitle}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {copy.subtitle(counts.all, counts.low_stock + counts.stock_out)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={scope} onValueChange={setScope}>
            <TabsList>
              <TabsTrigger data-testid="inventory-scope-all" value="all">{copy.all}</TabsTrigger>
              <TabsTrigger data-testid="inventory-scope-today" value="today">{copy.today}</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button data-testid="inventory-add-product" className="h-9" onClick={() => openCreatePage(query)}>
            <Plus className="h-4 w-4 mr-1" /> {copy.addProduct}
          </Button>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testId="inv-filter-all">
          {copy.all} <span className="font-mono ml-1">{counts.all}</span>
        </FilterPill>
        <FilterPill active={filter === "in_stock"} onClick={() => setFilter("in_stock")} testId="inv-filter-in-stock" tone="success">
          {copy.inStock} <span className="font-mono ml-1">{counts.in_stock}</span>
        </FilterPill>
        <FilterPill active={filter === "low_stock"} onClick={() => setFilter("low_stock")} testId="inv-filter-low" tone="warning">
          {copy.low} <span className="font-mono ml-1">{counts.low_stock}</span>
        </FilterPill>
        <FilterPill active={filter === "stock_out"} onClick={() => setFilter("stock_out")} testId="inv-filter-out" tone="danger">
          {copy.stockOut} <span className="font-mono ml-1">{counts.stock_out}</span>
        </FilterPill>
        <FilterPill active={filter === "setup_needed"} onClick={() => setFilter("setup_needed")} testId="inv-filter-setup" tone="warning">
          {parityCopy.setupNeeded} <span className="font-mono ml-1">{counts.setup_needed}</span>
        </FilterPill>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="inventory-search" data-barcode-input="true" placeholder={copy.searchPlaceholder} value={query} onChange={(e) => setQuery(e.target.value)} className="pl-10 h-10" />
        </div>
        <Button variant="outline" className="h-10" size="sm" data-testid="inventory-scan" onClick={() => {
          toast.info(copy.barcodeModeReady, { description: copy.barcodeModeReadyDescription });
          document.querySelector("[data-testid='inventory-search']")?.focus();
        }}>
          <ScanBarcode className="h-4 w-4 mr-1" /> {copy.scan}
        </Button>
      </div>

      {/* Table */}
      <div className="luxe-card rounded-2xl overflow-hidden">
        <div className="grid grid-cols-[minmax(0,1fr)_120px_120px_120px_120px_160px] items-center px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground border-b border-border/70 bg-muted/25">
          <div className="flex items-center gap-1.5">{copy.product} <ArrowUpDown className="h-3 w-3 opacity-50" /></div>
          <div>{copy.category}</div>
          <div className="text-right">{copy.stock}</div>
          <div className="text-right">{copy.soldTodayColumn}</div>
          <div className="text-right">{copy.price}</div>
          <div className="text-right">{copy.action}</div>
        </div>
        <div className="divide-y divide-border/60">
          {paginatedProducts.map((p) => {
            const status = productStockStatus(p);
            const setupNeeded = isProductSetupNeeded(p);
            const tutorialProduct = tutorial.active && tutorial.progress?.currentStep === "inventory_update" && (
              tutorial.focus.productId ? p.id === tutorial.focus.productId : setupNeeded
            );
            return (
              <div
                key={p.id}
                data-testid={`inventory-row-${p.id}`}
                data-tutorial-product={tutorialProduct ? "true" : undefined}
                onClick={() => {
                  if (tutorialProduct) {
                    tutorial.setStage("cost");
                    openEditPage(p);
                    return;
                  }
                  navigate(routeWithSearch(`/inventory/${p.id}`));
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    if (tutorialProduct) {
                      tutorial.setStage("cost");
                      openEditPage(p);
                    } else {
                      navigate(routeWithSearch(`/inventory/${p.id}`));
                    }
                  }
                }}
                className="grid cursor-pointer grid-cols-[minmax(0,1fr)_120px_120px_120px_120px_160px] items-center px-5 py-3.5 transition-colors hover:bg-accent/40"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <ProductArtwork
                    compact
                    isGeneratingImage={processingImageIds.has(p.id)}
                    onGenerateImage={openImageDialog}
                    product={p}
                    className="h-9 w-9 shrink-0 rounded-md"
                    imageClassName="rounded-md"
                    iconClassName="h-4 w-4 text-muted-foreground/60"
                  />
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="truncate text-sm font-medium">{p.name}</div>
                      {setupNeeded ? <SetupBadge copy={parityCopy} /> : null}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{p.barcode || copy.noBarcode}</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">{p.category}</div>
                <div className="text-right">
                  {setupNeeded ? <SetupBadge copy={parityCopy} /> : <StockBadge copy={copy} status={status} stock={p.stock} />}
                </div>
                <div className="text-right font-mono text-sm tabular-nums">{p.soldToday || 0}</div>
                <div className="text-right font-mono text-sm tabular-nums font-semibold">{formatCurrencyLocal(p.price, locale)}</div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 rounded-full border-primary/15 bg-primary/5 px-4 text-primary shadow-sm transition-colors hover:border-primary/30 hover:bg-primary/10"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(routeWithSearch(`/inventory/${p.id}`));
                    }}
                  >
                    {copy.viewDetails}
                  </Button>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
          <div className="px-5 py-16 text-center">
              <Boxes className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="text-sm text-muted-foreground">{copy.noProductsMatch}</div>
              {query && (
                <Button variant="outline" size="sm" className="mt-4" onClick={() => openCreatePage(query)}>
                  <Plus className="h-4 w-4 mr-1" /> {copy.addQuery(query)}
                </Button>
              )}
            </div>
          )}
        </div>
        {filtered.length > 0 && (
          <div className="flex items-center justify-between border-t border-border/70 bg-muted/10 px-5 py-3">
            <div className="text-sm text-muted-foreground">
              {copy.showing(
                Math.min((page - 1) * PAGE_SIZE + 1, filtered.length),
                Math.min(page * PAGE_SIZE, filtered.length),
                filtered.length
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9"
                disabled={page === 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                {copy.previous}
              </Button>
              <div className="min-w-[68px] text-center text-sm font-medium text-foreground">
                {page} / {totalPages}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9"
                disabled={page === totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                {copy.next}
              </Button>
            </div>
          </div>
        )}
      </div>

      </PageContainer>
      <ProductImageGenerateDialog
        open={Boolean(imageDialogProduct)}
        onOpenChange={(open) => {
          if (!open) {
            setImageDialogProduct(null);
          }
        }}
        onConfirm={handleGenerateProductImage}
        product={imageDialogProduct}
        balance={credits}
      />
    </>
  );
}

function createProductDraft(product = {}) {
  return {
    barcode: product.barcode || "",
    cost: getInventoryDraftNumber(product, "cost"),
    id: product.id || "",
    lastSoldAt: product.lastSoldAt || "",
    lowStockAt: product.lowStockAt === undefined ? "3" : String(product.lowStockAt),
    name: product.name || "",
    price: product.price === undefined ? "" : String(product.price),
    status: product.status || "Active",
    stock: getInventoryDraftNumber(product, "stock"),
    url: product.url || "",
    urlStatus: product.urlStatus || "pending",
  };
}

function FilterPill({ active, onClick, children, tone = "default", testId }) {
  const toneClass = active
    ? tone === "success" ? "border-success bg-success/10 text-success"
    : tone === "warning" ? "border-warning bg-warning/10 text-warning"
    : tone === "danger"  ? "border-destructive bg-destructive/10 text-destructive"
    : "border-primary bg-primary/10 text-primary"
    : "border-border bg-card text-foreground hover:border-foreground/30";
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${toneClass}`}
    >
      {children}
    </button>
  );
}

function StockBadge({ copy, status, stock }) {
  const map = {
    in_stock:   { c: "bg-success/15 text-success", l: `${stock} ${String(copy.inStock).toLowerCase()}` },
    low_stock:  { c: "bg-warning/15 text-warning", l: `${stock} · ${copy.low}` },
    stock_out:  { c: "bg-destructive/15 text-destructive", l: copy.stockOut },
    untracked:  { c: "bg-muted text-muted-foreground", l: copy.untracked },
  };
  const v = map[status];
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${v.c}`}>{v.l}</span>;
}

function SetupBadge({ copy }) {
  return (
    <span className="inline-block shrink-0 rounded-full bg-warning/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-warning">
      {copy.setupNeeded}
    </span>
  );
}

function NestedPageShell({ backLabel, children, onBack }) {
  return (
    <PageContainer className="!max-w-none">
      <div className="grid min-h-[calc(100dvh-190px)] content-center py-8">
        <div className="mx-auto w-full max-w-4xl">
          <button
            type="button"
            onClick={onBack}
            className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            {backLabel}
          </button>
          {children}
        </div>
      </div>
    </PageContainer>
  );
}

function ProductFormPage({ copy, draft, errors, formCopy, onBack, onFieldChange, onSave, parityCopy, saving, setupNeeded }) {
  const isEditing = Boolean(draft.id);

  return (
    <NestedPageShell backLabel={isEditing ? copy.backToProductDetails : copy.backToInventory} onBack={onBack}>
      <div className="overflow-hidden rounded-lg border border-border/70 bg-card shadow-[0_24px_70px_-48px_rgba(15,23,42,0.5)]">
        <div className="border-b border-border/45 px-6 py-5">
          <h1 className="text-2xl font-semibold tracking-tight">{isEditing ? copy.editProduct : copy.createProductTitle}</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {isEditing ? copy.formDescription : formCopy.formIntro}
          </p>
        </div>

        <div className="grid gap-3 bg-background px-6 py-6">
          {setupNeeded ? (
            <div className="flex gap-3 rounded-xl border border-warning/25 bg-warning/5 p-4">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <div>
                <div className="text-sm font-semibold text-foreground">{parityCopy.completeSetup}</div>
                <div className="mt-1 text-xs leading-5 text-muted-foreground">{parityCopy.setupBody}</div>
              </div>
            </div>
          ) : null}
          <PremiumField label={copy.productName} error={errors.name} required>
            <Input
              placeholder={copy.productNamePlaceholder}
              value={draft.name}
              onChange={(e) => onFieldChange("name", e.target.value)}
              className="h-10 rounded-lg border-border/70 bg-background text-[12px] font-medium shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </PremiumField>

          <div className="grid gap-4 md:grid-cols-2">
            <PremiumField label={copy.costPrice} optional={!setupNeeded} optionalLabel={copy.optional} error={errors.cost} required={setupNeeded}>
              <Input
                data-testid="tutorial-product-cost"
                placeholder="0"
                inputMode="numeric"
                value={draft.cost}
                onChange={(e) => onFieldChange("cost", e.target.value.replace(/[^0-9.]/g, ""))}
                className="h-10 rounded-lg border-border/70 bg-background font-mono text-[12px] font-semibold tabular-nums shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </PremiumField>
            <PremiumField label={copy.sellingPrice} error={errors.price} required>
              <Input
                placeholder="0"
                inputMode="numeric"
                value={draft.price}
                onChange={(e) => onFieldChange("price", e.target.value.replace(/[^0-9.]/g, ""))}
                className="h-10 rounded-lg border-border/70 bg-background font-mono text-[12px] font-semibold tabular-nums shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </PremiumField>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <PremiumField
              label={copy.openingStock}
              error={errors.stock}
              hint={formCopy.openingStockHint}
              required
            >
              <Input
                data-testid="tutorial-product-stock"
                placeholder="0"
                inputMode="numeric"
                value={draft.stock}
                onChange={(e) => onFieldChange("stock", e.target.value.replace(/[^0-9]/g, ""))}
                className="h-10 rounded-lg border-border/70 bg-background font-mono text-[12px] font-semibold tabular-nums shadow-[0_10px_24px_-22px_rgba(15,23,42,0.45)] focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </PremiumField>
            <button
              type="button"
              onClick={() => {
                toast.info(copy.scannerReady, { description: copy.scannerReadyDescription });
              }}
              className="flex min-h-[66px] items-center gap-3 rounded-lg border border-border/70 bg-muted/30 px-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus-visible:ring-0"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background text-primary shadow-sm">
                <ScanBarcode className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[12px] font-medium">{copy.optionalBarcode}</span>
                <span className="block truncate text-[12px] text-muted-foreground">
                  {draft.barcode ? draft.barcode : copy.scanToAttach}
                </span>
              </span>
            </button>
          </div>

        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/45 px-6 py-5">
          <Button variant="ghost" onClick={onBack} disabled={saving} className="h-9">
            {copy.cancel}
          </Button>
          <Button data-testid="tutorial-product-save" onClick={onSave} disabled={saving} className="h-9 justify-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {saving ? copy.saving : isEditing ? copy.saveProduct : copy.createProduct}
          </Button>
        </div>
      </div>
    </NestedPageShell>
  );
}

function ProductDetailPage({ allocationRows, archiveCopy, copy, currentView, isGeneratingImage = false, locale, onAddStock, onArchive, onBack, onEdit, onGenerateImage, onOpenView, product, sales }) {
  const [stockQuantity, setStockQuantity] = useState("");
  const [savingStock, setSavingStock] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [profitResult, setProfitResult] = useState(null);
  const profitForm = useForm({
    resolver: zodResolver(createProfitCalculatorSchema(copy)),
    mode: "onChange",
    defaultValues: {
      cost: product?.cost != null ? String(product.cost) : "",
      sellingPrice: product?.price != null ? String(product.price) : "",
      rangeStart: "",
      rangeEnd: "",
    },
  });
  const productTransactions = useMemo(() => (
    sales
      .flatMap((sale) => sale.items
        .filter((item) => item.productId === product.id)
        .map((item) => ({
          amount: item.quantity * item.unitPrice,
          balanceDue: sale.balanceDue,
          cashier: sale.cashier,
          createdAt: sale.createdAt,
          paymentStatus: sale.paymentStatus,
          quantity: item.quantity,
          saleId: sale.id,
          syncState: sale.syncState,
          total: sale.total,
          unitPrice: item.unitPrice,
        })))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  ), [product.id, sales]);
  useEffect(() => {
    setStockQuantity("");
  }, [currentView, product?.id]);

  useEffect(() => {
    profitForm.reset({
      cost: product?.cost != null ? String(product.cost) : "",
      sellingPrice: product?.price != null ? String(product.price) : "",
      rangeStart: "",
      rangeEnd: "",
    });
    setProfitResult(null);
  }, [product?.cost, product?.id, product?.price, profitForm]);

  if (!product) return null;
  const status = productStockStatus(product);
  const setupNeeded = isProductSetupNeeded(product);
  const localizedProductStatus = product.status === "Active" ? (copy.active || product.status) : product.status;
  const margin = product.cost > 0 ? Math.round(((product.price - product.cost) / product.price) * 100) : 0;
  const watchedProfitFields = profitForm.watch();
  const parsedRangeStart = watchedProfitFields.rangeStart ? new Date(`${watchedProfitFields.rangeStart}T00:00:00`) : null;
  const parsedRangeEnd = watchedProfitFields.rangeEnd ? new Date(`${watchedProfitFields.rangeEnd}T23:59:59.999`) : null;
  const isProfitRangeInvalid = Boolean(profitForm.formState.errors.rangeEnd);

  const computeProfitResult = ({ cost, rangeEnd, rangeStart, sellingPrice }) => {
    const costPerItem = Number(cost);
    const sellingPricePerItem = Number(sellingPrice);
    const start = rangeStart ? new Date(`${rangeStart}T00:00:00`) : null;
    const end = rangeEnd ? new Date(`${rangeEnd}T23:59:59.999`) : null;
    const filteredTransactions = productTransactions.filter((transaction) => {
      const createdAt = new Date(transaction.createdAt);
      if (start && createdAt < start) return false;
      if (end && createdAt > end) return false;
      return true;
    });
    const unitsSold = filteredTransactions.reduce((sum, item) => sum + item.quantity, 0);
    const revenue = filteredTransactions.reduce((sum, item) => sum + item.amount, 0);
    const costTotal = unitsSold * costPerItem;
    const sellingPriceTotal = Number.isFinite(sellingPricePerItem) ? sellingPricePerItem : 0;
    const projectedRevenue = unitsSold * sellingPricePerItem;
    const estimatedProfit = projectedRevenue - costTotal;

    return {
      costTotal,
      estimatedProfit,
      revenue,
      sellingPrice: sellingPriceTotal,
      unitsSold,
    };
  };

  const handleProfitCalculate = profitForm.handleSubmit((values) => {
    setProfitResult(computeProfitResult(values));
  });

  const submitStock = async () => {
    setSavingStock(true);
    try {
      await onAddStock(product.id, Number(stockQuantity || 0));
      toast.success(copy.stockUpdated);
      onOpenView("overview");
      setStockQuantity("");
    } catch (error) {
      toast.error(copy.addStockFailed, {
        description: error instanceof Error ? error.message : copy.tryAgain,
      });
    } finally {
      setSavingStock(false);
    }
  };

  return (
    <NestedPageShell backLabel={copy.backToInventory} onBack={onBack}>
      <div className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-[0_24px_70px_-48px_rgba(15,23,42,0.5)]">
        <div className="flex items-start justify-between gap-4 border-b border-border/60 px-5 py-4 sm:px-6">
          <div className="min-w-0">
            {currentView !== "overview" && (
              <button
                type="button"
                onClick={() => onOpenView("overview")}
                className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                {copy.productDetails}
              </button>
            )}
            <div className="flex items-center gap-3">
              <ProductArtwork
                compact
                isGeneratingImage={isGeneratingImage}
                onGenerateImage={onGenerateImage}
                product={product}
                className="hidden h-11 w-11 shrink-0 rounded-xl sm:flex"
                imageClassName="rounded-xl"
                iconClassName="h-5 w-5 text-primary"
              />
              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold tracking-tight">{currentView === "stock" ? copy.addStockView : currentView === "allocation" ? copy.allocation : currentView === "transactions" ? copy.transactions : currentView === "profit" ? copy.profitCalculator : product.name}</h2>
                <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
                  {product.barcode || copy.noBarcode} · {product.category}
                </p>
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onEdit(product)}
            className="h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
            aria-label={`${copy.editProduct} ${product.name}`}
            title={copy.editProduct}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>

        <div className="min-h-0 overflow-y-auto px-5 py-5 sm:px-6">
          {currentView === "overview" && setupNeeded ? (
            <div className="mb-5 flex flex-col gap-4 rounded-xl border border-warning/25 bg-warning/5 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                <div>
                  <div className="text-sm font-semibold text-foreground">{archiveCopy.setupNeeded}</div>
                  <div className="mt-1 text-xs leading-5 text-muted-foreground">{archiveCopy.setupBody}</div>
                </div>
              </div>
              <Button type="button" size="sm" onClick={() => onEdit(product)} className="shrink-0">
                {archiveCopy.completeSetup}
              </Button>
            </div>
          ) : null}
          {currentView === "overview" && (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div className="space-y-5">
                <div className="overflow-hidden rounded-xl border border-border/70 bg-muted/30">
                  <ProductArtwork
                    isGeneratingImage={isGeneratingImage}
                    onGenerateImage={onGenerateImage}
                    product={product}
                    className="aspect-square w-full rounded-none"
                    imageClassName="rounded-none"
                    iconClassName="h-12 w-12 text-muted-foreground/60"
                  />
                </div>
                <div className="rounded-xl border border-border/70 bg-muted/20 p-5 text-center">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{copy.currentStock}</div>
                  <div className="mt-1 font-mono text-5xl font-semibold tabular-nums tracking-tight">{product.stock ?? "—"}</div>
                  <div className="mt-3"><StockBadge copy={copy} status={status} stock={product.stock} /></div>
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  <Stat label={copy.sellingPrice} value={formatCurrencyLocal(product.price, locale)} mono />
                  <Stat label={copy.costPrice} value={formatCurrencyLocal(product.cost, locale)} mono />
                  <Stat label={copy.margin} value={`${margin}%`} mono />
                  <Stat label={copy.lowStockAt} value={product.lowStockAt} mono />
                  <Stat label={copy.soldToday} value={product.soldToday || 0} mono />
                  <Stat label={copy.status} value={localizedProductStatus} />
                </div>

                {status === "stock_out" && (
                  <div className="flex gap-3 rounded-xl border border-destructive/25 bg-destructive/5 p-4">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <div className="text-xs">
                      <div className="font-semibold text-destructive">{copy.outOfStock}</div>
                      <div className="mt-0.5 text-muted-foreground">{copy.lostPotentialSales(formatCurrencyLocal(product.price * 3, locale))}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => onOpenView("allocation")}
                  className="w-full rounded-xl border border-border/70 bg-card p-4 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">{copy.allocation}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{copy.stockAllocationBalance}</div>
                    </div>
                    <Layers className="h-4 w-4 text-primary" />
                  </div>
                  <div className="mt-4 space-y-2 text-xs">
                    {allocationRows.length
                      ? allocationRows.map((row) => <AllocationRow key={row.name} name={row.name} qty={row.qty} />)
                      : <div className="text-muted-foreground">{copy.untracked}</div>}
                  </div>
                </button>

                <Button data-testid="product-add-stock" onClick={() => onOpenView("stock")} className="h-10 w-full justify-center">
                  <Plus className="mr-1 h-4 w-4" /> {copy.addStock}
                </Button>
                <Button variant="outline" onClick={() => onOpenView("profit")} className="h-10 w-full justify-center gap-2">
                  <Calculator className="h-4 w-4" />
                  {copy.profitCalculator}
                </Button>
                <Button variant="outline" className="h-10 w-full justify-center gap-2" onClick={() => onOpenView("transactions")}>
                  <ReceiptText className="h-4 w-4" />
                  {copy.transactions}
                </Button>
                <Button variant="outline" className="h-10 w-full justify-center gap-2 text-destructive hover:text-destructive" onClick={() => setArchiveOpen(true)}>
                  <Trash2 className="h-4 w-4" />
                  {archiveCopy.archiveItem}
                </Button>
              </div>
            </div>
          )}

          {currentView === "stock" && (
            <div className="mx-auto grid min-h-[360px] max-w-md content-center gap-5 py-8">
              <div className="text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Plus className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-semibold tracking-tight">{copy.recordIncomingStock}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{copy.currentStockIs(product.stock ?? copy.untracked)}</p>
              </div>
              <Input
                autoFocus
                placeholder={copy.quantity}
                inputMode="numeric"
                value={stockQuantity}
                onChange={(e) => setStockQuantity(e.target.value.replace(/[^0-9]/g, ""))}
                className="h-12 text-center font-mono text-lg font-semibold tabular-nums"
              />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => onOpenView("overview")}>{copy.cancel}</Button>
                <Button onClick={submitStock} disabled={savingStock || Number(stockQuantity || 0) <= 0}>
                  {savingStock ? copy.saving : copy.addStock}
                </Button>
              </div>
            </div>
          )}

          {currentView === "allocation" && (
            <div className="mx-auto grid min-h-[360px] max-w-xl content-center gap-5 py-10">
              <div className="text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Layers className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-semibold tracking-tight">{copy.allocation}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{copy.allocationDescription}</p>
              </div>
              <div className="rounded-xl border border-border/70 bg-card p-4">
                <div className="space-y-3 text-sm">
                  {allocationRows.length
                    ? allocationRows.map((row) => <AllocationRow key={row.name} name={row.name} qty={row.qty} />)
                    : <div className="text-muted-foreground">{copy.untracked}</div>}
                </div>
              </div>
            </div>
          )}

          {currentView === "transactions" && (
            <div className="mx-auto grid max-w-5xl gap-5 py-6">
              {productTransactions.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-border/70 bg-card">
                  <div className="grid grid-cols-[minmax(150px,1fr)_70px_110px_120px_minmax(160px,1fr)_120px_88px] items-center gap-x-3 border-b border-border/60 bg-muted/25 px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    <div>{copy.transactionSaleId}</div>
                    <div className="text-right">{copy.qty}</div>
                    <div className="text-right">{copy.unitPrice}</div>
                    <div className="pr-2 text-right">{copy.amount}</div>
                    <div className="pl-2">{copy.cashier}</div>
                    <div className="text-right">{copy.transactionsWhen}</div>
                    <div className="text-right">{copy.transactionStatus}</div>
                  </div>
                  <div className="divide-y divide-border/60">
                    {productTransactions.map((transaction) => (
                      <div
                        key={`${transaction.saleId}-${transaction.createdAt}-${transaction.quantity}`}
                        className="grid grid-cols-[minmax(150px,1fr)_70px_110px_120px_minmax(160px,1fr)_120px_88px] items-center gap-x-3 px-5 py-4"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-xs text-muted-foreground font-mono">{transaction.saleId}</div>
                        </div>
                        <div className="text-right font-mono text-sm tabular-nums">{transaction.quantity}</div>
                        <div className="text-right font-mono text-sm tabular-nums">{formatCurrencyLocal(transaction.unitPrice, locale)}</div>
                        <div className="pr-2 text-right font-mono text-sm font-semibold tabular-nums">{formatCurrencyLocal(transaction.amount, locale)}</div>
                        <div className="min-w-0 pl-2">
                          <div className="truncate text-sm font-medium">{transaction.cashier}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{formatRelativeLocal(transaction.createdAt, locale)}</div>
                          <div className="text-xs text-muted-foreground font-mono">{formatTimeLocal(transaction.createdAt, locale)}</div>
                        </div>
                        <div className="text-right">
                          <PaymentBadge copy={copy} status={transaction.paymentStatus} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="min-h-[420px] rounded-xl border border-dashed border-border/80 bg-muted/15 px-6 py-14 text-center">
                  <ReceiptText className="mx-auto h-10 w-10 text-muted-foreground/50" />
                  <div className="mt-3 text-sm font-medium">{copy.noTransactionsYet}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{copy.noTransactionsYetDescription}</div>
                </div>
              )}
            </div>
          )}

          {currentView === "profit" && (
            <div className="mx-auto w-full max-w-5xl py-5">
              <Card className="overflow-hidden rounded-lg border-border/70 bg-gradient-to-br from-card via-card to-muted/20 shadow-[0_24px_80px_-60px_rgba(15,23,42,0.45)]">
                <CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1fr)_320px]">
                  <Form {...profitForm}>
                    <form onSubmit={handleProfitCalculate} className="space-y-6 p-6 lg:p-8">
                      <div className="grid gap-5 sm:grid-cols-2">
                        <FormField
                          control={profitForm.control}
                          name="cost"
                          render={({ field }) => (
                            <FormItem className="space-y-2">
                              <FormLabel className="text-xs font-semibold text-foreground">{copy.costPrice}</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  autoFocus
                                  inputMode="decimal"
                                  placeholder="0"
                                  className="h-11 rounded-lg bg-background/80 font-mono text-sm shadow-sm"
                                  onChange={(event) => field.onChange(event.target.value.replace(/[^0-9.]/g, ""))}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={profitForm.control}
                          name="sellingPrice"
                          render={({ field }) => (
                            <FormItem className="space-y-2">
                              <FormLabel className="text-xs font-semibold text-foreground">{copy.sellingPrice}</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  inputMode="decimal"
                                  placeholder="0"
                                  className="h-11 rounded-lg bg-background/80 font-mono text-sm shadow-sm"
                                  onChange={(event) => field.onChange(event.target.value.replace(/[^0-9.]/g, ""))}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <Separator />

                      <div className="grid gap-5 sm:grid-cols-2">
                        <FormField
                          control={profitForm.control}
                          name="rangeStart"
                          render={({ field }) => (
                            <FormItem className="space-y-2">
                              <FormLabel className="text-xs font-semibold text-foreground">{copy.from}</FormLabel>
                              <FormControl>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className={cn(
                                        "h-11 w-full justify-between rounded-lg bg-background/80 px-3 text-left text-sm font-normal shadow-sm",
                                        !field.value && "text-muted-foreground"
                                      )}
                                    >
                                      {field.value ? formatDateLocal(parseFormDate(field.value), locale) : copy.pickDate}
                                      <CalendarIcon className="h-4 w-4 opacity-70" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={parseFormDate(field.value)}
                                      onSelect={(date) => field.onChange(toFormDateValue(date))}
                                      initialFocus
                                    />
                                  </PopoverContent>
                                </Popover>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={profitForm.control}
                          name="rangeEnd"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <FormLabel className="text-xs font-semibold text-foreground">{copy.to}</FormLabel>
                          <FormControl>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className={cn(
                                    "h-11 w-full justify-between rounded-lg bg-background/80 px-3 text-left text-sm font-normal shadow-sm",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? formatDateLocal(parseFormDate(field.value), locale) : copy.pickDate}
                                  <CalendarIcon className="h-4 w-4 opacity-70" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={parseFormDate(field.value)}
                                  onSelect={(date) => field.onChange(toFormDateValue(date))}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                      </div>

                      <div className="flex flex-col-reverse gap-3 border-t border-border/60 pt-6 sm:flex-row sm:items-center sm:justify-between">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-10 justify-center px-4"
                          onClick={() => onOpenView("overview")}
                        >
                          {copy.cancel}
                        </Button>
                        <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-10 px-4"
                            onClick={() => {
                              profitForm.setValue("rangeStart", "", { shouldValidate: true, shouldDirty: true });
                              profitForm.setValue("rangeEnd", "", { shouldValidate: true, shouldDirty: true });
                            }}
                          >
                            {copy.clearRange}
                          </Button>
                          <Button type="submit" className="h-10 px-5" disabled={profitForm.formState.isSubmitting || isProfitRangeInvalid}>
                            {copy.calculate}
                          </Button>
                        </div>
                      </div>
                    </form>
                  </Form>

                  <div className="border-t border-border/70 bg-muted/20 p-6 lg:border-l lg:border-t-0 lg:p-8">
                    <div className="space-y-4">
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground">{copy.estimatedProfit}</div>
                        <div className="mt-2 font-mono text-3xl font-semibold tabular-nums text-primary">
                          {profitResult ? formatCurrencyLocal(profitResult.estimatedProfit, locale) : "—"}
                        </div>
                      </div>
                      <Separator />
                      <div className="space-y-3 text-sm">
                        <SummaryRow label={copy.profitRevenue} value={profitResult ? formatCurrencyLocal(profitResult.revenue, locale) : "—"} />
                        <SummaryRow label={copy.unitsSold} value={profitResult ? profitResult.unitsSold : "—"} />
                        <SummaryRow label={copy.profitCostTotal} value={profitResult ? formatCurrencyLocal(profitResult.costTotal, locale) : "—"} />
                        <SummaryRow label={copy.profitSellingPrice} value={profitResult ? formatCurrencyLocal(profitResult.sellingPrice, locale) : "—"} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
      <Dialog open={archiveOpen} onOpenChange={(open) => !archiving && setArchiveOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{archiveCopy.archiveItem}</DialogTitle>
            <DialogDescription>{archiveCopy.archiveBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" disabled={archiving} onClick={() => setArchiveOpen(false)}>{copy.cancel}</Button>
            <Button
              variant="destructive"
              disabled={archiving}
              onClick={async () => {
                setArchiving(true);
                try {
                  await onArchive(product);
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : copy.saveFailed);
                  setArchiving(false);
                }
              }}
            >
              {archiveCopy.archive}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </NestedPageShell>
  );
}

function PremiumField({ children, error, hint, label, optional = false, optionalLabel, required = false }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <label className="text-[12px] font-medium text-muted-foreground">{label}</label>
        {required ? <span className="text-[12px] font-semibold text-destructive">*</span> : null}
        {!required && optional ? <span className="text-[12px] text-muted-foreground/70">({optionalLabel})</span> : null}
      </div>
      {children}
      {hint && !error ? <div className="text-[11px] leading-4 text-muted-foreground">{hint}</div> : null}
      {error ? <div className="mt-2 text-[12px] text-destructive">{error}</div> : null}
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold mt-1 ${mono ? "font-mono tabular-nums" : ""}`}>{value}</div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function AllocationRow({ name, qty }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{name}</span>
      <span className="font-mono tabular-nums font-medium">{qty}</span>
    </div>
  );
}

function PaymentBadge({ copy, status }) {
  const map = {
    Paid: { className: "bg-success/15 text-success", label: copy.paymentPaid },
    "Partly paid": { className: "bg-warning/15 text-warning", label: copy.partlyPaid },
    Unpaid: { className: "bg-destructive/15 text-destructive", label: copy.paymentUnpaid },
  };
  const current = map[status] || { className: "bg-muted text-muted-foreground", label: status };

  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${current.className}`}>
      {current.label}
    </span>
  );
}
